import { ReactNode } from 'react'

import { clsx } from 'clsx'
import { TreeInformation, TreeItemRenderContext } from 'react-complex-tree'

import { useDynamicBlockByKey } from '@tribeplatform/react-sdk/hooks'
import { COLUMN_BLOCK_NAME } from '@tribeplatform/react-slate-kit/constants'
import {
  useSlate,
  useSlateKitContext,
} from '@tribeplatform/react-slate-kit/hooks'

import { EditorSlateExtraContext } from '../types.js'
import { BlockActions } from './BlockActions/index.js'
import { ColumnChildrenPlaceholder } from './ColumnChildrenPlaceholder.js'
import { renderDepthOffset } from './constants.js'
import { SlateTreeItemIcon } from './SlateTreeItemIcon.js'
import { SlateTreeItemInfo } from './types.js'

export type SlateTreeItemProps = {
  item: SlateTreeItemInfo
  depth: number
  context: TreeItemRenderContext<'expandedItems' | 'focusedItem'>
  info: TreeInformation
  title: ReactNode
  arrow: ReactNode
  children: ReactNode
}

export const SlateTreeItem = ({
  item,
  depth,
  children,
  title,
  context,
  arrow,
}: SlateTreeItemProps) => {
  const { kit } = useSlateKitContext()
  const {
    extraContext: {
      slate: {
        focusedBlock,
        selectedBlock,
        activateBlock,
        focusBlock,
        unfocusBlock,
      },
    },
  } = useSlate<EditorSlateExtraContext>()
  const itemName = item?.data?.name ?? ''
  const isColumnBlock = itemName === COLUMN_BLOCK_NAME

  const {
    data: {
      id,
      name,
      config: {
        description,
        acceptsChildren,
        lockedChildren,
        copyDisabled,
        editable,
        removable,
      },
      compiledProps: { appId, blockKey },
    },
  } = item
  const { startRenamingItem } = context

  const { data: dynamicBlock } = useDynamicBlockByKey({
    variables: { appId, key: blockKey },
    fields: { favicon: 'all', image: 'all' },
    useQueryOptions: { enabled: !!appId && !!blockKey },
  })

  const { name: dynamicBlockName } = kit.getDynamicBlock()
  const isDynamicBlock =
    dynamicBlock && dynamicBlockName && dynamicBlockName === name

  const focused = id === focusedBlock
  const selected = id === selectedBlock
  const itemAcceptsChildren = acceptsChildren && !lockedChildren

  return (
    <li
      {...context.itemContainerWithChildrenProps}
      className={clsx('flex flex-col')}
      onDoubleClick={e => {
        e.stopPropagation()
        activateBlock(item.data.id)
      }}
    >
      <div
        {...context.itemContainerWithoutChildrenProps}
        className={clsx(
          'flex flex-row gap-1 items-center w-full',
          'text-content-subdued',
          focused && 'bg-surface-hovered text-content',
          selected && 'bg-surface-pressed text-content',
          context.isDraggingOver && 'bg-action-primary text-content',
        )}
        onMouseEnter={() => focusBlock(id)}
        onMouseLeave={() => unfocusBlock(id)}
      >
        <div
          {...context.interactiveElementProps}
          className="py-2 px-2 w-full cursor-pointer"
        >
          <div
            className="flex flex-row items-center gap-1 grow"
            style={{ paddingInlineStart: `${depth * renderDepthOffset}px` }}
          >
            {arrow}
            <div
              className={clsx('overflow-hidden flex flex-1 flex-shrink-0', {
                'text-content-disabled': item.data.config.hide,
              })}
            >
              <div className="flex flex-row items-center gap-1 grow truncate">
                <div className="shrink-0">
                  <SlateTreeItemIcon
                    item={item}
                    isColumnBlock={isColumnBlock}
                    dynamicBlock={dynamicBlock}
                  />
                </div>
                <div className="grow truncate">
                  {isDynamicBlock ? dynamicBlock.name : title}
                  {description && (
                    <span className="ml-1 text-content-subdued text-xs">
                      {/* eslint-disable-next-line react/jsx-no-literals */}
                      {`(${description})`}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <BlockActions
              id={id}
              editable={editable}
              removable={removable}
              copyDisabled={copyDisabled}
              acceptsChildren={itemAcceptsChildren}
              hide={!focused && !selected}
              onRename={startRenamingItem}
              asDropdown
            />
          </div>
        </div>
      </div>
      {isColumnBlock && (
        <ColumnChildrenPlaceholder
          treeItem={item}
          isExpanded={context.isExpanded}
          indentation={(depth + 1) * renderDepthOffset}
        />
      )}
      {children}
    </li>
  )
}
